import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import StatusTag from '../../../components/ui/StatusTag';
import {
  useProductionOrder,
  useProductionOrderWorkflows,
  useUpdateProductionOrder,
} from '../../../hooks/useProductionOrderHooks';
import {
  ProductionOrder,
  ProductionSection,
  TransactionStatus,
} from '../../../types/ProductionOrder';
import { isLocked } from './planning-board.util';

const { Text } = Typography;
const DATE = 'YYYY-MM-DD';

interface Props {
  orderId?: string;
  open: boolean;
  onClose: () => void;
}

const PlanningOrderDrawer: React.FC<Props> = ({ orderId, open, onClose }) => {
  const { data, isLoading } = useProductionOrder(orderId);
  const { data: workflow } = useProductionOrderWorkflows();
  const update = useUpdateProductionOrder();
  const [draft, setDraft] = useState<ProductionOrder | null>(null);

  useEffect(() => {
    if (data) setDraft(structuredClone(data));
  }, [data]);

  const locked = isLocked(draft?.status);

  const statusOptions = useMemo(() => {
    const fromWorkflow = (workflow ?? []).map((w) => w.status);
    const set = new Set<TransactionStatus>([
      ...fromWorkflow,
      'DRAFT',
      'CONFIRM',
      'APPROVED',
      'DONE',
      'CANCEL',
    ]);
    if (draft?.status) set.add(draft.status);
    return Array.from(set).map((s) => ({ value: s, label: s }));
  }, [workflow, draft?.status]);

  const patch = (p: Partial<ProductionOrder>) =>
    setDraft((d) => (d ? { ...d, ...p } : d));

  const patchSection = (idx: number, p: Partial<ProductionSection>) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            sections: d.sections.map((s, i) => (i === idx ? { ...s, ...p } : s)),
          }
        : d,
    );

  const onSave = () => {
    if (!draft) return;
    update.mutate(draft, {
      onSuccess: () => {
        message.success(`Production order ${draft.code} updated`);
        onClose();
      },
      onError: (e) =>
        message.error(
          e.response?.data?.message ?? 'Failed to update production order',
        ),
    });
  };

  return (
    <Drawer
      title={draft ? `Production Order · ${draft.code}` : 'Production Order'}
      width={720}
      open={open}
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose}>Close</Button>
          <Button
            type={'primary'}
            loading={update.isPending}
            disabled={locked || !draft}
            onClick={onSave}
          >
            Save
          </Button>
        </Space>
      }
    >
      {isLoading || !draft ? (
        <div className={'flex justify-center py-10'}>
          <Spin />
        </div>
      ) : (
        <div className={'space-y-4'}>
          {locked && (
            <Alert
              type={'info'}
              showIcon
              message={`This order is ${draft.status} and is read-only.`}
            />
          )}

          <div className={'grid grid-cols-2 gap-4'}>
            <Field label={'Code'}>
              <Text strong>{draft.code}</Text>
            </Field>
            <Field label={'Status'}>
              <Select
                value={draft.status}
                disabled={locked}
                options={statusOptions}
                style={{ width: '100%' }}
                onChange={(v) => patch({ status: v as TransactionStatus })}
              />
            </Field>
            <Field label={'Production Start'}>
              <DatePicker
                style={{ width: '100%' }}
                disabled={locked}
                value={draft.productionStart ? dayjs(draft.productionStart) : null}
                onChange={(d) =>
                  patch({ productionStart: d ? d.format(DATE) : null })
                }
              />
            </Field>
            <Field label={'Production End'}>
              <DatePicker
                style={{ width: '100%' }}
                disabled={locked}
                value={draft.productionEnd ? dayjs(draft.productionEnd) : null}
                onChange={(d) => patch({ productionEnd: d ? d.format(DATE) : null })}
              />
            </Field>
            <Field label={'Source'}>
              <Text>{draft.source || '—'}</Text>
            </Field>
            <Field label={'Description'}>
              <Text>{draft.description || '—'}</Text>
            </Field>
          </div>

          <Field label={'Notes'}>
            <Input.TextArea
              rows={2}
              disabled={locked}
              value={draft.notes ?? ''}
              onChange={(e) => patch({ notes: e.target.value })}
            />
          </Field>

          <div>
            <Text strong>Sections</Text>
            <Table<ProductionSection>
              className={'mt-2'}
              size={'small'}
              rowKey={(r) => r.id ?? r.section?.code}
              dataSource={draft.sections}
              pagination={false}
              columns={[
                {
                  title: 'Section',
                  dataIndex: ['section', 'code'],
                  render: (_v, r) => r.section?.code ?? r.section?.description ?? '—',
                },
                {
                  title: 'Start',
                  render: (_v, r, idx) => (
                    <DatePicker
                      size={'small'}
                      disabled={locked}
                      value={r.productionStart ? dayjs(r.productionStart) : null}
                      onChange={(d) =>
                        patchSection(idx, {
                          productionStart: d ? d.format(DATE) : null,
                        })
                      }
                    />
                  ),
                },
                {
                  title: 'End',
                  render: (_v, r, idx) => (
                    <DatePicker
                      size={'small'}
                      disabled={locked}
                      value={r.productionEnd ? dayjs(r.productionEnd) : null}
                      onChange={(d) =>
                        patchSection(idx, {
                          productionEnd: d ? d.format(DATE) : null,
                        })
                      }
                    />
                  ),
                },
                {
                  title: 'Status',
                  render: (_v, r, idx) =>
                    locked ? (
                      <StatusTag status={r.status} />
                    ) : (
                      <Select
                        size={'small'}
                        style={{ width: 130 }}
                        value={r.status}
                        options={statusOptions}
                        onChange={(v) =>
                          patchSection(idx, { status: v as TransactionStatus })
                        }
                      />
                    ),
                },
              ]}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <div className={'text-xs text-gray-500 mb-1'}>{label}</div>
    {children}
  </div>
);

export default PlanningOrderDrawer;
